import { inject, Injectable } from '@angular/core';
import {
	Auth,
	GoogleAuthProvider,
	signInWithEmailAndPassword,
	signInWithPopup,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { LogService } from './log.service';
import { LogType } from '@interfaces/log.model';
import { Timestamp } from '@angular/fire/firestore';
import { MessageService } from 'primeng/api';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly auth = inject(Auth);
	private readonly router = inject(Router);
	private readonly logService = inject(LogService);
	private readonly userService = inject(UserService);
	private readonly messageService = inject(MessageService);

	public async googleLogin() {
		return signInWithPopup(this.auth, new GoogleAuthProvider())
			.then(async (result) => {
				await this.userService.evalUserLogin(result.user);
				this.router.navigate(['home']);
			})
			.catch((error) => {
				console.error({ error });
				const detail =
					error.code === 'auth/admin-restricted-operation'
						? 'Tu cuenta no ha sido creada. Por favor acercate a la caja para crearla.'
						: 'Hubo un error al iniciar sesión. Por favor intenta nuevamente.';
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail,
				});
				this.router.navigate(['auth']);
			});
	}

	public async login(credentials: { email: string; password: string }) {
		return signInWithEmailAndPassword(
			this.auth,
			credentials.email,
			credentials.password
		)
			.then(async (result) => {
				await this.userService.evalUserLogin(result.user);
				this.router.navigate(['home']);
			})
			.catch((error) => {
				console.error(error);
				const detail =
					error.code === 'auth/admin-restricted-operation'
						? 'Tu cuenta no ha sido creada. Por favor acercate a la caja para crearla.'
						: 'Error al iniciar sesión. Por favor verificá tu usuario y contraseña.';
				this.messageService.add({
					severity: 'error',
					summary: 'Error',
					detail,
				});
				this.router.navigate(['auth']);
			});
	}

	public async logout(forced?: boolean): Promise<void> {
		const user = this.userService.user();
		if (user) {
			await this.logService.saveLog(
				user,
				LogType.Logout,
				forced
					? `User ${user.email} updated version and logged out`
					: `User ${user.email} logged out`,
				Timestamp.now()
			);
		}
		await this.auth.signOut();
		this.router.navigate(['auth']);
	}
}
